import { useQuery, useMutation, useQueryClient, type Query } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  CmsOrganizationAccessSettingsResponse,
  UpdateOrganizationAccessSettingsRequest,
} from "@/types/generated/cms.generated";
import { errorMessage } from "@/lib/errorMessage";

/**
 * Per-organization sign-in & access settings (Athlyn-owned):
 *  - `allowExternalUsers` — when off, the employee app hides guest sign-in and the client portal
 *    disables Public visibility;
 *  - `perksEnabled` — when off, perks are hidden everywhere for the organization;
 *  - `healthOnboardingEnabled` — when off, the app doesn't ask for health at sign-in (it still asks
 *    when someone joins a challenge that needs health data);
 *  - `chatEnabled` — when off, the organization's employees have no chat and nothing is sent to the
 *    chat provider; the read-only `chatSwitchInProgress` stays true until the Worker has muted /
 *    unmuted the organization's chats after a switch.
 * Backed by the CMS API `GET/PUT /organizations/{id}/access-settings`. No stored row ⇒ the API
 * reports all four as true. The portal always sends all four flags (the API treats a missing
 * `healthOnboardingEnabled` / `chatEnabled` as "keep the stored value", for older portals).
 */

export const accessSettingsKey = (organizationId: string | null | undefined) =>
  ["organizations", organizationId, "access-settings"] as const;

/** How often the settings are re-read while the Worker is still applying a chat switch. */
export const CHAT_SWITCH_POLL_MS = 15_000;

/**
 * Poll while the latest data says a chat switch is still being applied, so the card's
 * "Applying" note clears on its own; stop as soon as `chatSwitchInProgress` is false.
 */
export function accessSettingsRefetchInterval(
  query: Query<CmsOrganizationAccessSettingsResponse>,
): number | false {
  return query.state.data?.chatSwitchInProgress === true ? CHAT_SWITCH_POLL_MS : false;
}

export function useOrgAccessSettings(organizationId: string | null | undefined) {
  return useQuery<CmsOrganizationAccessSettingsResponse>({
    queryKey: accessSettingsKey(organizationId),
    queryFn: () =>
      apiRequest("GET", `/organizations/${organizationId}/access-settings`),
    enabled: !!organizationId,
    refetchInterval: accessSettingsRefetchInterval,
  });
}

export function useUpdateOrgAccessSettings(
  organizationId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateOrganizationAccessSettingsRequest) =>
      apiRequest<CmsOrganizationAccessSettingsResponse>(
        "PUT",
        `/organizations/${organizationId}/access-settings`,
        data,
      ),
    // On-success cache write (not optimistic) — a failed PUT never leaves stale UI. The switches
    // are disabled while the mutation is pending.
    onSuccess: (updated) => {
      queryClient.setQueryData(accessSettingsKey(organizationId), updated);
      queryClient.invalidateQueries({ queryKey: accessSettingsKey(organizationId) });
      toast({
        title: "Access settings updated",
        description: `External users ${updated.allowExternalUsers ? "allowed" : "not allowed"} · Perks ${updated.perksEnabled ? "on" : "off"} · Health onboarding ${updated.healthOnboardingEnabled === false ? "off" : "on"} · Chat ${updated.chatEnabled === false ? "off" : "on"}.`,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description:
          errorMessage(error, "Failed to update access settings. Please try again."),
        variant: "destructive",
      });
    },
  });
}
